import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    OnInit,
    afterNextRender,
    computed,
    inject,
    input,
    linkedSignal,
    signal,
    viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonListHeader,
    IonSearchbar,
    IonSpinner,
    IonTitle,
    IonToolbar,
    ModalController,
    SearchbarCustomEvent,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { closeOutline, searchOutline } from 'ionicons/icons';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { ParametersService } from '@app/components/parameters/parameters.service';
import { ProductRangeService } from '@app/components/poetpro/productrange/productrange.service';
import { ProductSearchService } from './product-search.service';
import {
    PRODUCT_SEARCH_DEBOUNCE_MS,
    PRODUCT_SEARCH_MIN_LENGTH,
    PRODUCT_SEARCH_SUGGESTION_LIMIT,
    ProductSearchResult,
    emptyGroupedResults,
    idleSearchState,
} from './product-search.models';
import { ProductSearchResultItemComponent } from './product-search-result-item.component';

@Component({
    selector: 'app-product-search-mobile-modal',
    templateUrl: './product-search-mobile-modal.component.html',
    styleUrl: './product-search-mobile-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TranslateModule,
        IonHeader,
        IonToolbar,
        IonTitle,
        IonButtons,
        IonButton,
        IonIcon,
        IonContent,
        IonSearchbar,
        IonList,
        IonListHeader,
        IonItem,
        IonLabel,
        IonSpinner,
        ProductSearchResultItemComponent,
    ],
})
export class ProductSearchMobileModalComponent implements OnInit {
    private readonly searchService = inject(ProductSearchService);
    private readonly productRangeService = inject(ProductRangeService);
    private readonly parameterService = inject(ParametersService);
    private readonly router = inject(Router);
    private readonly modalCtrl = inject(ModalController);

    readonly initialQuery = input('');
    readonly searchbar = viewChild<IonSearchbar>('searchbar');
    private readonly injector = inject(Injector);

    readonly query = signal('');

    private readonly locale = toSignal(
        this.parameterService.getParametersObservable().pipe(
            map(params => params.user?.locale ?? params.country?.code)
        )
    );

    private readonly flatTree = this.productRangeService.flatPhTree(this.locale);

    private readonly debouncedQuery = toSignal(
        toObservable(this.query).pipe(
            map(q => q.trim()),
            distinctUntilChanged(),
            debounceTime(PRODUCT_SEARCH_DEBOUNCE_MS)
        ),
        { initialValue: '' }
    );

    readonly state = computed(() => {
        const query = this.debouncedQuery();
        if (query.length < PRODUCT_SEARCH_MIN_LENGTH) {
            return idleSearchState(query);
        }
        if (this.flatTree.error()) {
            return { status: 'error' as const, query, results: emptyGroupedResults(query) };
        }
        if (!this.flatTree.hasValue()) {
            return { status: 'loading' as const, query, results: emptyGroupedResults(query) };
        }
        return this.searchService.toState(this.flatTree.value(), query, PRODUCT_SEARCH_SUGGESTION_LIMIT);
    });

    readonly activeIndex = linkedSignal({
        source: this.state,
        computation: () => -1,
    });

    readonly flatOptions = computed(() => {
        const { machines, variants } = this.state().results;
        return [...machines, ...variants];
    });

    constructor() {
        addIcons({ closeOutline, searchOutline });
    }

    ngOnInit(): void {
        this.query.set(this.initialQuery() || '');
        afterNextRender(() => {
            // Ionic needs a tick after the modal searchbar is in the DOM.
            setTimeout(() => void this.searchbar()?.setFocus(), 50);
        }, { injector: this.injector });
    }

    onIonInput(event: SearchbarCustomEvent): void {
        this.query.set(event.detail.value ?? '');
    }

    clearQuery(): void {
        this.query.set('');
        void this.searchbar()?.setFocus();
    }

    onSearchButtonClick(): void {
        if (this.query().trim().length >= PRODUCT_SEARCH_MIN_LENGTH) {
            this.showAllResults();
        } else {
            void this.searchbar()?.setFocus();
        }
    }

    dismiss(): void {
        this.modalCtrl.dismiss({ query: this.query() });
    }

    onResultSelected(result: ProductSearchResult): void {
        this.modalCtrl.dismiss({ query: this.query() }).then(() => {
            this.router.navigate(result.routerLink, { queryParams: result.queryParams });
        });
    }

    showAllResults(): void {
        const q = this.query().trim();
        if (!q || q.length < PRODUCT_SEARCH_MIN_LENGTH) {
            return;
        }
        this.modalCtrl.dismiss({ query: q }).then(() => {
            this.router.navigate(['/products', 'search'], { queryParams: { query: q } });
        });
    }

    onKeydown(event: KeyboardEvent): void {
        const options = this.flatOptions();
        const includeShowAll =
            this.state().status === 'results' || this.state().status === 'empty';
        const maxIndex = includeShowAll ? options.length : options.length - 1;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.activeIndex.set(Math.min(this.activeIndex() + 1, Math.max(maxIndex, 0)));
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.activeIndex.set(Math.max(this.activeIndex() - 1, -1));
                break;
            case 'Enter':
                event.preventDefault();
                this.activateCurrent();
                break;
            case 'Escape':
                event.preventDefault();
                this.dismiss();
                break;
        }
    }

    isOptionActive(index: number): boolean {
        return this.activeIndex() === index;
    }

    isShowAllActive(): boolean {
        return this.activeIndex() === this.flatOptions().length;
    }

    private activateCurrent(): void {
        const options = this.flatOptions();
        const index = this.activeIndex();
        if (index >= 0 && index < options.length) {
            this.onResultSelected(options[index]);
            return;
        }
        if (index === options.length || (index < 0 && this.query().trim().length >= PRODUCT_SEARCH_MIN_LENGTH)) {
            this.showAllResults();
        }
    }
}
